import { useState } from "react";
import { useNavigate } from "react-router";
import { useTexts } from "../texts";
import { ChatIcon } from "../ui/icons/Icon";
import { PageHead } from "../ui/kit";
import { PageState, ServiceState } from "../ui/pieces";
import { scrollPageTop } from "../ui/scroll";
import { availabilityTone, capabilityText, pathCheckText, plural, priceLine, routeName, type PathCheck } from "./ai";
import { ChangePriceWindow, type PricedPath } from "./ChangePriceWindow";
import { ModelPickWindow } from "./ModelPickWindow";
import { useAiData, useTitle } from "./parts";
import { providerHref } from "./ProviderPage";
import { joinAnd } from "./ProvidersPage";
import { Mark, MobileActions, RowsSkeleton, Section } from "./widgets";
import styles from "./ai.module.css";

type Path = PathCheck & {
  providerModelId: number;
  providerCode: string;
  providerName: string | null;
  status: string;
  capabilities: string;
  inputPrice: number | null;
  outputPrice: number | null;
  cachedPrice?: number | null;
  priceCurrency: string | null;
  executable: boolean;
};

/** `usedIn`: las rutas donde responde alguna de sus vías (el mismo dato que «En uso», E107). */
type Model = {
  modelId: number;
  modelCode: string;
  modelName: string;
  inUse: boolean;
  usedIn?: string[];
  paths: Path[];
};

/**
 * IA › Modelos › ← modelo (§5.9 `ia-modelo`), con una sola cara con la lista (E48): el subtítulo dice en cuántos
 * proveedores está disponible y, si está en uso, en qué rutas (enlace a Rutas). Una fila por proveedor (C17): su nombre con
 * la disponibilidad C55 de la lista, las capacidades con la misma función de la lista y el precio completo (C24 1.14.11,
 * con la entrada en caché) o «Sin precio». Toda la fila abre la ficha del proveedor en su pestaña Modelos; «Cambiar precio»
 * abre su ventana sin abrir la ficha (E123). «Usar en una ruta» abre Cambiar modelo con este modelo elegido. La tabla
 * técnica de antes no va (D7).
 */
export function ModelPage({ id }: { id: number }) {
  const { L, locale } = useTexts();
  const navigate = useNavigate();
  const { data, state, retry, reload } = useAiData<Model>(`/api/admin/ai/models/${id}`);
  const [pick, setPick] = useState(false);
  const [priceFor, setPriceFor] = useState<PricedPath | null>(null);
  useTitle(data?.modelName ?? L("ADM_AI_MODELS"));
  const back = { href: "/Ai/Modelos/Index", label: L("ADM_AI_MODELS") };
  if (state === "missing" || state === "failed")
    return (
      <PageState
        head={<PageHead title={L("ADM_AI_MODEL")} back={back} />}
        title={L(state === "missing" ? "ADM_AI_MODEL_MISSING" : "ADM_UNAVAILABLE")}
        line={L(state === "missing" ? "ADM_AI_MODEL_MISSING_LINE" : "ADM_UNAVAILABLE_LINE")}
        action={
          state === "missing" ? (
            <a className="tm-btn secondary" href="/Ai/Modelos/Index">
              {L("ADM_AI_GO_MODELS")}
            </a>
          ) : (
            <button type="button" className="tm-btn secondary" onClick={retry}>
              {L("ADM_RETRY")}
            </button>
          )
        }
      />
    );
  if (!data)
    return (
      <>
        <PageHead title={L("ADM_AI_MODEL")} back={back} />
        <RowsSkeleton rows={3} />
      </>
    );

  const paths = data.paths;
  // Una vía que el proveedor rechazó (No disponible, script 166) no se ofrece para una ruta.
  const executable =
    paths.find((path) => path.executable && path.status === "ENABLED" && path.availability !== "UNAVAILABLE") ?? null;
  const blockedBy = paths.some((path) => path.executable && path.status === "ENABLED")
    ? L("ADM_AI_UNAVAILABLE")
    : L("ADM_AI_NOT_EXECUTABLE_LINE");
  // La ruta que le corresponde: Embeddings si solo hace embeddings; si no, Texto.
  const embeddingOnly = paths.every(
    (path) => /EMBEDDING/.test(path.capabilities) && !/\bTEXT\b/.test(path.capabilities),
  );
  const route = embeddingOnly ? "EMBEDDING" : "TEXT_CHAT";
  const usedIn = (data.usedIn ?? []).map((code) => routeName(L, code));
  // «Usado en {0}»: solo las rutas van como enlace a Rutas.
  const [usedBefore, usedAfter = ""] = L("ADM_AI_USED_IN").split("{0}");

  return (
    <>
      <PageHead
        title={data.modelName}
        back={back}
        tag={
          data.inUse ? (
            <span className={styles.tagAttention} data-part="en-uso">
              {L("ADM_AI_IN_USE")}
            </span>
          ) : undefined
        }
        sub={
          <span data-part="subtitulo">
            {plural(L, locale, paths.length, "ADM_AI_IN_N_PROVIDERS_ONE", "ADM_AI_IN_N_PROVIDERS")}
            {usedIn.length > 0 ? (
              <>
                {" · "}
                {usedBefore}
                <a className={styles.usedInLink} href="/Ai/Rutas">
                  {joinAnd(L, usedIn)}
                </a>
                {usedAfter}
              </>
            ) : null}
          </span>
        }
      >
        <button
          type="button"
          className="tm-btn primary"
          disabled={!executable}
          title={executable ? undefined : blockedBy}
          onClick={() => setPick(true)}
        >
          {L("ADM_AI_USE_IN_ROUTE")}
        </button>
      </PageHead>
      <Section id="model-paths" title={L("ADM_AI_BY_PROVIDER")}>
        <ul className={styles.list}>
          {paths.map((path) => {
            const name = path.providerName ?? L("USAGE_UNKNOWN");
            const state = availabilityTone(L, path.availability);
            const href = providerHref(path.providerCode, "modelos");
            const price = priceLine(
              L,
              locale,
              { input: path.inputPrice, cached: path.cachedPrice, output: path.outputPrice },
              path.priceCurrency,
            );
            // D2 (decisión del dueño): cómo se agregó el modelo es otro dato, en gris junto a las capacidades; nunca en
            // la etiqueta de disponibilidad (C55 1.14.12, E125).
            const caps = [
              capabilityText(L, path.capabilities),
              path.executable ? null : L("ADM_AI_NOT_EXECUTABLE_LINE"),
              path.addedManually ? L("ADM_AI_ADDED_MANUALLY") : null,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <li
                key={path.providerModelId}
                className={styles.providerRow}
                data-part="fila-proveedor"
                onClick={(event) => {
                  // Toda la fila abre la ficha; «Cambiar precio» y el enlace se atienden solos.
                  if ((event.target as Element).closest("a,button")) return;
                  navigate(href);
                  scrollPageTop();
                }}
              >
                <a className={styles.providerLink} href={href}>
                  <Mark name={name} />
                  <span className={styles.rowText}>
                    <span className={styles.line1}>
                      <strong>{name}</strong>
                      {path.executable ? (
                        // Cómo y cuándo se supo la disponibilidad (script 166), al pasar sobre el estado.
                        <span className={styles.stateHint} title={pathCheckText(L, locale, path) ?? undefined}>
                          <ServiceState tone={state.tone}>{state.text}</ServiceState>
                        </span>
                      ) : (
                        <ServiceState tone="warn">{L("ADM_AI_NOT_EXECUTABLE")}</ServiceState>
                      )}
                      {path.status === "ENABLED" ? null : (
                        <span className={styles.tagNeutral}>{L("ADM_EMP_INACTIVE")}</span>
                      )}
                    </span>
                    <span className={styles.line2}>{caps}</span>
                    <span
                      className={price || !data.inUse ? styles.priceLine : `${styles.priceLine} ${styles.noPrice}`}
                      data-part="precio-linea"
                    >
                      {price ?? L("ADM_AI_NO_PRICE_CAP")}
                    </span>
                  </span>
                </a>
                <button
                  type="button"
                  className={`tm-btn secondary ${styles.changePrice}`}
                  data-part="cambiar-precio"
                  onClick={() =>
                    setPriceFor({
                      providerModelId: path.providerModelId,
                      providerName: name,
                      modelName: data.modelName,
                      inputPrice: path.inputPrice,
                      cachedPrice: path.cachedPrice ?? null,
                      outputPrice: path.outputPrice,
                    })
                  }
                >
                  {L("ADM_AI_CHANGE_PRICE")}
                </button>
                <span className={styles.providerChevron} aria-hidden="true">
                  <ChatIcon name="chevron-right" />
                </span>
              </li>
            );
          })}
        </ul>
      </Section>
      <MobileActions>
        <button type="button" className="tm-btn primary" disabled={!executable} onClick={() => setPick(true)}>
          {L("ADM_AI_USE_IN_ROUTE")}
        </button>
      </MobileActions>
      {priceFor ? (
        <ChangePriceWindow
          path={priceFor}
          onClose={() => setPriceFor(null)}
          onSaved={() => {
            setPriceFor(null);
            void reload();
          }}
        />
      ) : null}
      {pick ? (
        <ModelPickWindow
          open
          mode={route === "EMBEDDING" ? "embedding" : "replace"}
          route={route}
          organizationId={null}
          scopeName={null}
          preselect={executable?.providerModelId ?? null}
          onClose={() => setPick(false)}
          onDone={() => {
            setPick(false);
            navigate(`/Ai/Rutas?route=${route}`);
          }}
        />
      ) : null}
    </>
  );
}
